/**
 * SessionsList - Scrollable list of agent sessions in the sidebar
 *
 * Shows active sessions at the top and a collapsible "Archived" section immediately below them.
 */

import React, { useState } from 'react';
import { Box, Typography, Button, Collapse } from '@mui/material';
import {
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import SessionItem from './SessionItem';

/**
 * SessionsList Component
 *
 * @param {Object} props
 * @param {Array} props.sessions - Array of session objects
 * @param {string|null} props.currentSessionId - Active session ID
 * @param {Function} props.onSelectSession - Select a session by ID
 * @param {Function} props.onArchiveSession - Archive a session by ID
 * @param {Function} props.onRestoreSession - Restore an archived session by ID
 * @param {Function} props.onDeleteSession - Permanently delete a session by ID
 */
const SessionsList = ({ sessions, currentSessionId, onSelectSession, onArchiveSession, onRestoreSession, onDeleteSession }) => {
  const [showArchived, setShowArchived] = useState(false);

  const activeSessions = sessions.filter((s) => s.status === 'active');
  const archivedSessions = sessions.filter((s) => s.status === 'archived');

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Active sessions */}
      {activeSessions.length === 0 ? (
        <Box className="sidebar-empty">
          <ChatBubbleOutlineIcon sx={{ fontSize: '2rem', opacity: 0.4 }} />
          <Typography variant="caption" color="text.secondary">
            No conversations yet. Start a new one above.
          </Typography>
        </Box>
      ) : (
        <Box className="sessions-list" role="list" aria-label="Conversation sessions">
          {activeSessions.map((session) => (
            <SessionItem
              key={session.id}
              session={session}
              isActive={session.id === currentSessionId}
              onSelect={onSelectSession}
              onArchive={onArchiveSession}
              isArchived={false}
            />
          ))}
        </Box>
      )}

      {/* Archived sessions toggle */}
      {archivedSessions.length > 0 && (
        <Box sx={{ mt: 1, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Button
            size="small"
            onClick={() => setShowArchived((v) => !v)}
            endIcon={showArchived ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              px: 1.5,
              py: 0.75,
              textTransform: 'none',
              fontSize: '0.75rem',
              color: 'text.secondary'
            }}
          >
            Archived ({archivedSessions.length})
          </Button>
          <Collapse in={showArchived}>
            <Box role="list" aria-label="Archived sessions">
              {archivedSessions.map((session) => (
                <SessionItem
                  key={session.id}
                  session={session}
                  isActive={session.id === currentSessionId}
                  onSelect={onSelectSession}
                  onArchive={onArchiveSession}
                  onRestore={onRestoreSession}
                  onDelete={onDeleteSession}
                  isArchived={true}
                />
              ))}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default SessionsList;
